import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Custom translation hook that provides additional functionality
 * @param {string} namespace - The namespace to use for translations
 * @returns {object} Translation functions and utilities
 */
export const useTranslation = (namespace = 'common') => {
  const { t, i18n } = useI18nTranslation(namespace);

  /**
   * Translate a key with optional parameters
   * @param {string} key - Translation key
   * @param {object} options - Translation options
   * @returns {string} Translated text
   */
  const translate = (key, options = {}) => {
    try {
      return t(key, options);
    } catch (error) {
      return key;
    }
  };

  /**
   * Get current language
   * @returns {string} Current language code
   */
  const getCurrentLanguage = () => {
    return i18n?.language || 'en';
  };

  /**
   * Check if current language is Hindi
   * @returns {boolean} True if current language is Hindi
   */
  const isHindi = () => {
    return i18n?.language === 'hi';
  };

  /**
   * Check if current language is English
   * @returns {boolean} True if current language is English
   */
  const isEnglish = () => {
    return i18n?.language === 'en';
  };

  /**
   * Change language
   * @param {string} language - Language code to change to
   * @returns {Promise} Promise that resolves when language change is complete
   */
  const changeLanguage = (language) => {
    if (i18n && i18n.isInitialized) {
      localStorage.setItem('i18nextLng', language);
      return i18n.changeLanguage(language).then(() => {
        // Set HTML lang attribute for CSS targeting
        document.documentElement.setAttribute('lang', language);
      }).catch((error) => {
        throw error;
      });
    } else {
      // Retry after a short delay
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (i18n && i18n.isInitialized) {
            localStorage.setItem('i18nextLng', language);
            i18n.changeLanguage(language).then(() => {
              // Set HTML lang attribute for CSS targeting
              document.documentElement.setAttribute('lang', language);
              resolve();
            }).catch((error) => {
              reject(error);
            });
          } else {
            reject(new Error('i18n not initialized'));
          }
        }, 100);
      });
    }
  };

  /**
   * Get language display name
   * @param {string} language - Language code
   * @returns {string} Display name for the language
   */
  const getLanguageDisplayName = (language) => {
    const languageNames = {
      en: 'English',
      hi: 'हिन्दी'
    };
    return languageNames[language] || language;
  };

  /**
   * Get available languages
   * @returns {array} Array of available language objects
   */
  const getAvailableLanguages = () => {
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' }
    ];
  };

  return {
    t: translate,
    i18n: i18n || { language: 'en' },
    getCurrentLanguage,
    isHindi,
    isEnglish,
    changeLanguage,
    getLanguageDisplayName,
    getAvailableLanguages
  };
};

export default useTranslation;
